import * as React from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PopoverContent } from '@/shared/components/ui/popover';
import { AsyncComboboxOptionItem } from './AsyncComboboxOption';
import type { AsyncComboboxOption } from './AsyncCombobox.types';

interface AsyncComboboxContentProps<T extends AsyncComboboxOption> {
  options: T[];
  selectedIds: string[];
  highlightedIndex: number;
  isMulti: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  search: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onSearch: (value: string) => void;
  onSelect: (option: T) => void;
  fetchNextPage: () => void;
  renderOption?: (option: T, selected: boolean) => React.ReactNode;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  optionRefs: React.RefObject<(HTMLDivElement | null)[]>;
}

export function AsyncComboboxContent<T extends AsyncComboboxOption>({
  options,
  selectedIds,
  highlightedIndex,
  isMulti,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  search,
  searchPlaceholder,
  emptyMessage,
  onSearch,
  onSelect,
  fetchNextPage,
  renderOption,
  onKeyDown,
  optionRefs,
}: AsyncComboboxContentProps<T>) {
  const { t } = useTranslation('asyncCombobox');
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  const resolvedSearchPlaceholder = searchPlaceholder ?? t('searchPlaceholder');
  const resolvedEmptyMessage = emptyMessage ?? t('empty');

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <PopoverContent
      className="p-0"
      style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '12rem' }}
      align="start"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      {/* Search input */}
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          autoFocus
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={resolvedSearchPlaceholder}
          className="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>

      {/* Options list */}
      <div className="max-h-60 overflow-y-auto p-1" role="listbox">
        {isLoading ? (
          <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('loading')}
          </div>
        ) : options.length === 0 ? (
          <div className="py-4 text-sm text-center text-muted-foreground">
            {resolvedEmptyMessage}
          </div>
        ) : (
          <>
            {options.map((option, index) => (
              <div
                key={option.id}
                ref={(el) => {
                  if (optionRefs.current) optionRefs.current[index] = el;
                }}
              >
                <AsyncComboboxOptionItem
                  option={option as AsyncComboboxOption}
                  isSelected={selectedIds.includes(option.id)}
                  isHighlighted={index === highlightedIndex}
                  isMulti={isMulti}
                  onClick={() => onSelect(option)}
                  renderOption={
                    renderOption ? (opt, sel) => renderOption(opt as T, sel) : undefined
                  }
                />
              </div>
            ))}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-2 gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('loadingMore')}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PopoverContent>
  );
}
